import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Portal-launcher payload for the ATTMOS Suite Unification surface
// (M9.5). Mirrors the wire shape in
// ``src/flowstack/server/routes/apps.py::PortalView``.
//
// Phase A: launcher only — clicking a card opens the portal in a new
// tab (``window.open(url, '_blank', 'noopener,noreferrer')``). Iframe
// embedding is deferred to P1.0+; the ``embed`` field is preserved so
// a later phase can switch on it without API churn.

export interface PortalEmbed {
  hostname: string;
  path: string;
}

export interface PortalOwnerContact {
  email: string;
  team: string;
}

export interface PortalIcon {
  path: string;
  /**
   * Optional dark-theme variant; falls back to ``path`` when omitted.
   * Same pattern as the FlowStack Header's theme-aware logo swap.
   *
   * Always ``string`` or explicitly ``null`` on the wire — never
   * ``undefined``. Per Rupert PR-360 r0 #3: the Python ``Icon`` model
   * defaults to ``None`` and serializes as JSON ``null``, so the
   * field is always present. Dropping the ``?`` modifier keeps call
   * sites checking exactly one absence-shape.
   */
  path_dark: string | null;
  alt: string;
}

/**
 * Resolve which icon source to use for the current FlowStack theme.
 * Extracted as a pure helper (no React hooks) so the theme-swap
 * behavior can be unit-tested deterministically — per Rupert PR-360
 * r0 #2 (the SPA's Zustand theme store can't be driven from a
 * component test, but this helper can be).
 */
export function resolveIconSrc(icon: PortalIcon, resolvedTheme: "light" | "dark"): string {
  const path = resolvedTheme === "dark" && icon.path_dark ? icon.path_dark : icon.path;
  return `/${path}`;
}

export interface Portal {
  slug: string;
  display_name: string;
  description: string;
  tagline: string | null;
  embed: PortalEmbed;
  icon: PortalIcon;
  owner_contact: PortalOwnerContact;
}

export interface AppsListResponse {
  apps: Portal[];
}

export interface PortalCatalogEntry extends Portal {
  /**
   * Whether the current session is entitled to launch this portal.
   * Powers the M9.5 Phase E entitlement panel in Settings: entitled
   * portals get an "Open" link; non-entitled get a "Request access"
   * mailto pointing at the portal's owner_contact.email.
   */
  entitled: boolean;
}

export interface AppsCatalogResponse {
  apps: PortalCatalogEntry[];
}

export const APPS_QUERY_KEY = ["apps", "list"] as const;
export const APPS_CATALOG_QUERY_KEY = ["apps", "catalog"] as const;

/**
 * Allowed eTLD+1 suffix for portal launch URLs. Mirrors the
 * server-side gate in ``flowstack/portals/_schema.py`` — defense in
 * depth so a manifest that somehow bypassed the server validator
 * (forward-compat schema drift, server-side bug) can't render an
 * anchor that points off-domain.
 */
const ATTMOS_DOMAIN_SUFFIX = ".attmosdiscovery.com";

/**
 * Build the absolute URL the launcher card opens. Always HTTPS — every
 * sibling portal is on the ``attmosdiscovery.com`` eTLD+1 and serves
 * TLS in production. Local-dev portals running over HTTP would need a
 * separate code path; not in M9.5 scope.
 *
 * Returns `null` when the manifest's embed target fails a client-side
 * sanity check (non-DNS-label hostname, off-domain suffix, or a URL
 * that doesn't round-trip through `new URL()` with an https scheme).
 * Per Rupert PR-351 r0 #3: defense-in-depth against a `javascript:` /
 * `data:` URI somehow reaching the client through a future
 * forward-compat schema variant. `target="_blank"` with a
 * `javascript:` href can still execute in some browsers even with
 * `rel="noopener noreferrer"`.
 */
export function portalLaunchUrl(portal: Portal): string | null {
  const candidate = `https://${portal.embed.hostname}${portal.embed.path}`;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (!url.hostname.endsWith(ATTMOS_DOMAIN_SUFFIX)) return null;
  // Reject the bare apex as a portal host — same defense-in-depth as
  // the server-side validator.
  if (url.hostname === ATTMOS_DOMAIN_SUFFIX.slice(1)) return null;
  // Reject any URL carrying userinfo (`user@host`) — a phishing
  // vector even when the URL parser resolves the trailing hostname
  // correctly. The server-side DNS-label regex already excludes the
  // `@` character, so reaching this branch is forward-compat / bug
  // territory.
  if (url.username !== "" || url.password !== "") return null;
  return url.href;
}

export function useApps() {
  return useQuery({
    queryKey: APPS_QUERY_KEY,
    queryFn: async (): Promise<Portal[]> => {
      const response = await apiFetch<AppsListResponse>("/api/apps");
      return response.apps;
    },
    // Same 60s staleTime as the session query — entitlement changes
    // are bounded by the session refresh cadence anyway (anchor §5.1).
    staleTime: 60_000,
  });
}

/**
 * Full catalog of portals + per-portal entitlement state. Powers the
 * Settings entitlement panel (Phase E / #347). Distinct from
 * {@link useApps} which returns only the entitled subset.
 */
export function useAppsCatalog() {
  return useQuery({
    queryKey: APPS_CATALOG_QUERY_KEY,
    queryFn: async (): Promise<PortalCatalogEntry[]> => {
      const response = await apiFetch<AppsCatalogResponse>("/api/apps/catalog");
      return response.apps;
    },
    staleTime: 60_000,
  });
}
